import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import HeaderSearch from "./HeaderSearch";
import ProfilePanel from "./ProfilePanel";
import { agentApi } from "../../lib/apiClient";
import { unwrapData } from "../../lib/catalog";
import { canAccessFinance } from "../../lib/agencyAccess";
import { FINANCE_UPDATED_EVENT } from "../../lib/alerts";

const HEADER_SHORTCUTS = [
  { label: "Packages", path: "/packages", icon: "solar:box-linear" },
  { label: "Hotels", path: "/hotels", icon: "solar:buildings-2-linear" },
  { label: "Transport", path: "/transportation", icon: "solar:bus-linear" },
  { label: "Flights", path: "/flights", icon: "ri-flight-takeoff-line" },
];

function formatInr(value) {
  const amount = Number(String(value ?? 0).replace(/,/g, ""));
  const safe = Number.isFinite(amount) ? amount : 0;
  return `₹ ${safe.toLocaleString("en-IN")}`;
}

export default function Header({ collapsed, onToggleSidebar, onToggleMobile, agencyRole = "" }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [walletBalance, setWalletBalance] = useState(0);
  const showWallet = canAccessFinance(agencyRole);

  useEffect(() => {
    let alive = true;

    agentApi
      .get("/travel-partner/notifications/unread-count")
      .then((payload) => {
        if (!alive) return;
        const count = Number(payload?.data?.unreadCount ?? payload?.unreadCount ?? 0);
        setUnreadCount(Number.isFinite(count) ? count : 0);
      })
      .catch(() => {
        if (alive) setUnreadCount(0);
      });

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!showWallet) {
      setWalletBalance(0);
      return undefined;
    }

    let alive = true;
    function loadWallet() {
      agentApi
        .get("/travel-partner/finance/account")
        .then((payload) => {
          if (!alive) return;
          const account = unwrapData(payload);
          const amount = Number(
            account?.wallet_balance ?? account?.walletBalance ?? account?.balance ?? 0
          );
          setWalletBalance(Number.isFinite(amount) ? amount : 0);
        })
        .catch(() => {
          if (alive) setWalletBalance(0);
        });
    }

    loadWallet();
    window.addEventListener(FINANCE_UPDATED_EVENT, loadWallet);
    return () => {
      alive = false;
      window.removeEventListener(FINANCE_UPDATED_EVENT, loadWallet);
    };
  }, [showWallet]);

  return (
    <div className="navbar-header">
      <div className="row align-items-center g-2 g-lg-3 navbar-header-row">
        <div className="col-auto min-w-0">
          <div className="d-flex flex-nowrap align-items-center gap-2 navbar-header-start">
            <button
              type="button"
              className={`sidebar-toggle ${collapsed ? "active" : ""}`}
              onClick={onToggleSidebar}
              aria-label="Toggle sidebar"
            >
              <iconify-icon
                icon={collapsed ? "iconoir:arrow-right" : "heroicons:bars-3-solid"}
                className="icon text-2xl"
              ></iconify-icon>
            </button>
            <button type="button" className="sidebar-mobile-toggle" onClick={onToggleMobile} aria-label="Open sidebar">
              <iconify-icon icon="heroicons:bars-3-solid" className="icon"></iconify-icon>
            </button>
            <HeaderSearch agencyRole={agencyRole} />
          </div>
        </div>
        <div className="col min-w-0 d-none d-lg-flex">
          <nav className="navbar-shortcuts" aria-label="Quick links">
            {HEADER_SHORTCUTS.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `navbar-shortcut${isActive ? " is-active" : ""}`}
                title={item.label}
              >
                <span className="navbar-shortcut-icon" aria-hidden="true">
                  <iconify-icon icon={item.icon}></iconify-icon>
                </span>
                <span className="navbar-shortcut-label">{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="col-auto">
          <div className="d-flex flex-nowrap align-items-center gap-2 gap-md-3 navbar-header-actions">
             {showWallet ? (
              <Link
                to="/wallet-credit"
                className="navbar-wallet"
                title="Wallet balance"
                aria-label={`Wallet balance ${formatInr(walletBalance)}`}
              >
                <span className="navbar-wallet-icon" aria-hidden="true">
                  <iconify-icon icon="solar:wallet-outline"></iconify-icon>
                </span>
                <span className="navbar-wallet-amount">{formatInr(walletBalance)}</span>
              </Link>
            ) : null}
            <Link
              to="/notifications"
              className="navbar-action-btn w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center position-relative"
              title="Notifications"
              aria-label="Notifications"
            >
              <iconify-icon icon="solar:bell-outline" className="icon text-xl"></iconify-icon>
              {unreadCount > 0 ? (
                <span className="navbar-unread-badge badge rounded-pill bg-danger">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              ) : null}
            </Link>
           
            <ProfilePanel />
          </div>
        </div>
      </div>
    </div>
  );
}
